// Add/Edit Organization Page (Separate Page, not modal)
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, Navigate } from 'react-router-dom';
import { Row, Col, Space, Button, notification } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import {
  ThemeButton,
  ThemeCard,
  ThemeFilterBtn,
  ThemeForm,
  ThemeInput,
  ThemeMainWrapper,
} from '../Styles/Theme';
import MobHeader from '../../Components/Header/MobHeader';
import { GreetingText } from '../Dashboard/DashboardStyle';
import {
  createOrganizationApi,
  createOrganizationReset,
  updateOrganizationApi,
  updateOrganizationReset,
  getOrganizationByIdApi,
  getOrganizationByIdReset,
} from '../../store/actions/organizationAction';
import { useOrganizationPermissions } from '../../utils/permissions';

const AddEditOrganization = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  // Permission check - shortcut hook (must be before any conditional returns)
  const { can, isSuperAdmin } = useOrganizationPermissions();

  const {
    createOrganizationState,
    updateOrganizationState,
    getOrganizationByIdState,
  } = useSelector((state) => state.organization);

  const [form] = ThemeForm.useForm();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    contact_person: '',
    contact_person_email: '',
    contact_person_mobile: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    is_active: true,
  });

  // Fetch organization data if editing
  useEffect(() => {
    if (isEdit) {
      dispatch(getOrganizationByIdApi(id));
    }
  }, [dispatch, id, isEdit]);

  // Handle organization data response
  useEffect(() => {
    if (getOrganizationByIdState.apiState === 'success' && getOrganizationByIdState.data) {
      const org = getOrganizationByIdState.data;
      const orgData = {
        name: org.name || '',
        contact_person: org.contact_person || '',
        contact_person_email: org.contact_person_email || '',
        contact_person_mobile: org.contact_person_mobile || '',
        address: org.address || '',
        city: org.city || '',
        state: org.state || '',
        pincode: org.pincode || '',
        is_active: org.is_active !== undefined ? org.is_active : true,
      };
      setFormData(orgData);
      form.setFieldsValue(orgData);
    } else if (getOrganizationByIdState.apiState === 'error') {
      notification.error({
        message: 'Error',
        description: getOrganizationByIdState.message || 'Failed to fetch organization',
      });
      dispatch(getOrganizationByIdReset());
      navigate('/organizations');
    }
  }, [getOrganizationByIdState, dispatch, form, navigate]);

  // Handle create response
  useEffect(() => {
    if (createOrganizationState.apiState === 'success') {
      notification.success({
        message: 'Success',
        description: 'Organization created successfully',
      });
      dispatch(createOrganizationReset());
      navigate('/organizations');
    } else if (createOrganizationState.apiState === 'error') {
      notification.error({
        message: 'Error',
        description: createOrganizationState.message || 'Failed to create organization',
      });
      dispatch(createOrganizationReset());
    }
  }, [createOrganizationState, dispatch, navigate]);

  // Handle update response
  useEffect(() => {
    if (updateOrganizationState.apiState === 'success') {
      notification.success({
        message: 'Success',
        description: 'Organization updated successfully',
      });
      dispatch(updateOrganizationReset());
      navigate('/organizations');
    } else if (updateOrganizationState.apiState === 'error') {
      notification.error({
        message: 'Error',
        description: updateOrganizationState.message || 'Failed to update organization',
      });
      dispatch(updateOrganizationReset());
    }
  }, [updateOrganizationState, dispatch, navigate]);

  const handleSubmit = (values) => {
    setLoading(true);
    const submitData = {
      ...values,
      is_active: values.is_active !== undefined ? values.is_active : true,
    };

    if (isEdit) {
      dispatch(updateOrganizationApi(id, submitData));
    } else {
      dispatch(createOrganizationApi(submitData));
    }
    setLoading(false);
  };

  // Permission check - redirect if no permission for create/edit (must be after all hooks)
  const requiredPermission = isEdit ? 'edit' : 'create';
  if (!can(requiredPermission)) {
    return <Navigate to="/organizations" replace />;
  }

  return (
    <>
      <MobHeader />
      <ThemeMainWrapper
        className="hasHeader"
        title={
          <Space gap={10}>
            <ThemeFilterBtn
              className="shadow"
              onClick={() => navigate('/organizations/')}
              size="middle"
              icon={<img src="/images/back-icon.svg" alt="Back" />}
            />
            <GreetingText>
              {isEdit ? 'Edit Organization' : 'Add Organization'}
            </GreetingText>
          </Space>
        }
      >
        <ThemeCard>
          <ThemeForm
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={formData}
          >
            <Row gutter={[15, 30]}>
              <Col xs={24} sm={12} md={12} lg={8}>
                <ThemeForm.Item
                  label="Name"
                  name="name"
                  rules={[
                    { required: true, message: 'Please enter Hospital name' },
                  ]}
                >
                  <ThemeInput size="large" placeholder="Enter Hospital Name" />
                </ThemeForm.Item>
              </Col>

              <Col xs={24} sm={12} md={12} lg={8}>
                <ThemeForm.Item
                  label="Contact Person"
                  name="contact_person"
                  rules={[
                    { required: true, message: 'Please enter Contact Person' },
                  ]}
                >
                  <ThemeInput size="large" placeholder="Enter Contact Person" />
                </ThemeForm.Item>
              </Col>

              <Col xs={24} sm={12} md={12} lg={8}>
                <ThemeForm.Item
                  label="Contact Person Email Address"
                  name="contact_person_email"
                  rules={[
                    {
                      type: 'email',
                      message: 'Please enter a valid email address',
                    },
                    { required: true, message: 'Please enter email address' },
                  ]}
                >
                  <ThemeInput
                    size="large"
                    placeholder="Enter Contact Person Email Address"
                    disabled={isEdit && !isSuperAdmin}
                  />
                </ThemeForm.Item>
              </Col>

              <Col xs={24} sm={12} md={12} lg={8}>
                <ThemeForm.Item
                  label="Contact Person Mobile Number"
                  name="contact_person_mobile"
                  rules={[
                    { required: true, message: 'Please enter Mobile Number' },
                  ]}
                >
                  <ThemeInput
                    size="large"
                    placeholder="Enter Contact Person Mobile Number"
                  />
                </ThemeForm.Item>
              </Col>

              <Col xs={24} sm={12} md={12} lg={8}>
                <ThemeForm.Item label="Address" name="address">
                  <ThemeInput size="large" placeholder="Enter Address" />
                </ThemeForm.Item>
              </Col>

              <Col xs={24} sm={12} md={12} lg={8}>
                <ThemeForm.Item label="State" name="state">
                  <ThemeInput size="large" placeholder="Select State" />
                </ThemeForm.Item>
              </Col>

              <Col xs={24} sm={12} md={12} lg={8}>
                <ThemeForm.Item label="City" name="city">
                  <ThemeInput size="large" placeholder="Enter City" />
                </ThemeForm.Item>
              </Col>

              <Col xs={24} sm={12} md={12} lg={8}>
                <ThemeForm.Item label="Pincode" name="pincode">
                  <ThemeInput size="large" placeholder="Enter Pincode" />
                </ThemeForm.Item>
              </Col>
            </Row>

            <Row gutter={[16, 16]} className="mt-4">
              <Col xs={24} className="text-end">
                <Space>
                  <ThemeButton
                    className="noBackground"
                    size="large"
                    onClick={() => navigate('/organizations')}
                  >
                    Cancel
                  </ThemeButton>
                  <ThemeButton
                    size="large"
                    type="primary"
                    htmlType="submit"
                    loading={
                      loading ||
                      createOrganizationState.apiState === 'loading' ||
                      updateOrganizationState.apiState === 'loading'
                    }
                  >
                    {isEdit ? 'Submit' : 'Submit'}
                  </ThemeButton>
                </Space>
              </Col>
            </Row>
          </ThemeForm>
        </ThemeCard>
      </ThemeMainWrapper>
    </>
  );
};

export default AddEditOrganization;
